import { Component, Input, forwardRef, signal, AfterViewInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { ControlValueAccessor, NG_VALUE_ACCESSOR, FormsModule, ReactiveFormsModule, AbstractControl } from '@angular/forms';

@Component({
  selector: 'app-input',
  imports: [CommonModule, FormsModule, ReactiveFormsModule],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => InputComponent),
      multi: true
    }
  ],
  templateUrl: './input.component.html',
  styleUrl: './input.component.css'
})
export class InputComponent implements ControlValueAccessor, AfterViewInit {
  @Input() type: string = 'text';
  @Input() label: string = '';
  @Input() placeholder: string = '';
  @Input() required: boolean = false;
  @Input() disabled: boolean = false;
  @Input() errorMessage: string = '';
  @Input() helpText: string = '';
  @Input() id: string = '';
  @Input() name: string = '';
  @Input() class: string = '';
  @Input() control?: AbstractControl | null;
  @Input() showPasswordToggle: boolean = false;

  value = signal<string>('');
  showPassword = signal(false);
  touched = signal(false);

  private onChange = (value: string) => {};
  private onTouched = () => {};

  ngAfterViewInit(): void {
    // Control can be passed as input for validation display
  }

  onInput(event: Event): void {
    const target = event.target as HTMLInputElement;
    const newValue = target.value;
    this.value.set(newValue);
    this.onChange(newValue);
    this.markAsTouched();
  }

  onBlur(): void {
    this.markAsTouched();
    this.onTouched();
  }

  togglePasswordVisibility(): void {
    this.showPassword.update(value => !value);
  }

  private markAsTouched(): void {
    if (!this.touched()) {
      this.touched.set(true);
    }
  }

  // ControlValueAccessor implementation
  writeValue(value: string): void {
    this.value.set(value || '');
  }

  registerOnChange(fn: (value: string) => void): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    this.disabled = isDisabled;
  }

  get inputType(): string {
    if (this.type === 'password' && this.showPassword()) {
      return 'text';
    }
    return this.type;
  }

  get hasError(): boolean {
    if (this.control) {
      return (this.control.invalid && (this.control.touched || this.touched())) || !!this.errorMessage;
    }
    return this.touched() && !!this.errorMessage;
  }

  get displayError(): string {
    if (this.errorMessage) {
      return this.errorMessage;
    }
    if (this.control && this.control.errors) {
      if (this.control.errors['required']) {
        return `${this.label || 'This field'} is required`;
      }
      if (this.control.errors['email']) {
        return 'Please enter a valid email address';
      }
      if (this.control.errors['minlength']) {
        return `Minimum length is ${this.control.errors['minlength'].requiredLength} characters`;
      }
      if (this.control.errors['maxlength']) {
        return `Maximum length is ${this.control.errors['maxlength'].requiredLength} characters`;
      }
    }
    return '';
  }

  get inputClasses(): string {
    const classes = [
      // Base styles
      'w-full',
      'px-4',
      'py-3',
      'border',
      'rounded-lg',
      'text-base',
      'bg-white',
      'text-gray-900',
      'transition-all',
      'duration-200',
      'box-border',
      'focus:outline-none',
      'placeholder:text-gray-400',
      'dark:bg-gray-700',
      'dark:text-gray-100',
      'dark:border-gray-600',
      'dark:placeholder:text-gray-400'
    ];

    // Password input padding adjustment
    if (this.type === 'password' && this.showPasswordToggle) {
      classes.push('pr-12');
    }
    
    // Error state
    if (this.hasError) {
      classes.push('border-red-500', 'focus:border-red-500', 'focus:ring-2', 'focus:ring-red-100');
    } else {
      classes.push('border-gray-300', 'focus:border-blue-600', 'focus:ring-2', 'focus:ring-blue-100');
    }
    
    // Disabled state
    if (this.disabled) {
      classes.push('opacity-60', 'cursor-not-allowed', 'bg-gray-50', 'dark:bg-gray-800');
    }

    // Custom classes
    if (this.class) {
      classes.push(this.class);
    }

    return classes.join(' ');
  }
}
